import React from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { tokens } from "../theme";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";

const UpdateMarketerModal = ({ open, setOpen, marketerData, onUpdate }) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { success, snackError } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const handleFormSubmit = async (values) => {
    const { firstName, lastName, email, phoneNumber } = values;

    let formattedPhone = phoneNumber;
    if (phoneNumber.startsWith('0')) {
      formattedPhone = '234' + phoneNumber.slice(1);
    } else if (phoneNumber.startsWith('+')) {
      formattedPhone = phoneNumber.slice(1);
    } else if (!phoneNumber.startsWith('234')) {
      formattedPhone = '234' + phoneNumber;
    }

    if (formattedPhone.length !== 13 || !formattedPhone.startsWith('234')) {
      snackError("Phone number must be 13 digits and start with 234");
      return;
    }

    try {
      const response = await axiosPrivate.put(
        "/card-service/cms/admin/marketers/update",
        {
          email,
          firstName,
          lastName,
          phoneNumber: formattedPhone,
        }
      );
      if (response?.data?.status === true) {
        success(response?.data?.message || "Marketer details updated successfully!");
        if (onUpdate) onUpdate();
        handleClose();
      } else {
        snackError(response?.data?.message || "Failed to update marketer");
      }
    } catch (error) {
      snackError(error?.response?.data?.message || "An error occurred while updating marketer");
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Split fullName to firstName and lastName
  let initialFirstName = "";
  let initialLastName = "";
  if (marketerData?.fullName) {
    const [first, ...rest] = marketerData.fullName.split(" ");
    initialFirstName = first || "";
    initialLastName = rest.join(" ") || "";
  }

  const initialValues = {
    firstName: initialFirstName,
    lastName: initialLastName,
    email: marketerData?.marketerUserEmail || "",
    phoneNumber: marketerData?.phoneNumber || "",
  };

  const checkoutSchema = yup.object().shape({
    firstName: yup.string().required("Required"),
    lastName: yup.string().required("Required"),
    email: yup.string().email().required("Required"),
    phoneNumber: yup.string().required("Required"),
  });

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          width: "50%",
          backgroundColor: colors.primary[400],
          p: 2,
          position: "relative",
          top: "50%",
          left: "55%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={checkoutSchema}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box padding={5}>
                <Typography
                  variant="h3"
                  sx={{ textAlign: "center", marginBottom: "30px" }}
                >
                  Update Marketer Details
                </Typography>
                <Box
                  display="grid"
                  gap="20px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": {
                      gridColumn: isNonMobile ? undefined : "span 4",
                    },
                    marginBottom: "10px",
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="First Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.firstName}
                    name="firstName"
                    error={!!touched.firstName && !!errors.firstName}
                    helperText={touched.firstName && errors.firstName}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Last Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.lastName}
                    name="lastName"
                    error={!!touched.lastName && !!errors.lastName}
                    helperText={touched.lastName && errors.lastName}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email}
                    name="email"
                    disabled
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Phone"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.phoneNumber}
                    name="phoneNumber"
                    error={!!touched.phoneNumber && !!errors.phoneNumber}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  sx={{ width: "100%", p: 2, mt: 2 }}
                >
                  Update Marketer
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default UpdateMarketerModal;
